import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { MinusIcon, PlusIcon } from '@synergy/theme-next';
import { styled } from '@mui/material';

import { PoolIconButton } from '../buttons/pool-icon-button/pool-icon-button';
import { PoolNumberTextfield } from '../pool-number-textfield/pool-number-textfield';
import { PoolTextfieldProps } from '../pool-textfield';

const StyledPoolTextfield = styled(PoolNumberTextfield)(({ theme }) => ({
    marginTop: 0,
    minWidth: 128,

    '& .MuiOutlinedInput-root': {
        height: 40,
        width: 150,
        padding: 0,
        [theme.breakpoints.down('sm')]: {
            width: 'auto',
        },
    },
    '& .MuiOutlinedInput-input': {
        textAlign: 'center',
        paddingTop: 9,
        paddingBottom: 9,
    },
    '& .MuiIconButton-root': {
        color: theme.palette.text.primary,
        '&:disabled': {
            color: theme.palette.text.disabled,
        },
    },

    '& .MuiFormHelperText-root': {
        display: 'none',
    },
}));

export type PoolQuantityPickerProps = PoolTextfieldProps & {
    onQuantityChange: (quantity: number) => void;
    quantity?: number;
    minQuantity?: number;
    maxQuantity?: number;
    interval?: number;
};

export const PoolQuantityPicker = (props: PoolQuantityPickerProps): ReactElement => {
    const {
        minQuantity = 1,
        interval = 1,
        maxQuantity = Number.MAX_VALUE,
        onQuantityChange,
        ...poolTextfieldProps
    } = props;
    const [quantity, setQuantity] = useState(props.quantity ?? minQuantity);

    useEffect(() => {
        setQuantity(props.quantity ?? minQuantity);
    }, [props.quantity, minQuantity]);

    const handleValueChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = +event.target.value;
        if (!value) {
            return;
        }
        setQuantity(value);
    };

    /**
     * Ensure quantity is rounded to the nearest interval above minimum quantity and make sure that
     * the maximum quantity is not exceeded.
     */
    const updateQuantity = (withIntervalCheck: boolean, quantityDiff?: number): void => {
        let nextQuantity: number;

        if (withIntervalCheck || minQuantity === interval) {
            if (quantityDiff) {
                if (quantityDiff > 0) {
                    const currentStep = Math.floor(quantity / interval);
                    nextQuantity = (currentStep + 1) * interval;
                } else {
                    const currentStep = Math.ceil(quantity / interval);
                    nextQuantity = (currentStep - 1) * interval;
                }
            } else {
                nextQuantity = Math.round(quantity / interval) * interval;
            }
        } else {
            nextQuantity = quantity;
        }

        if (nextQuantity < minQuantity) {
            nextQuantity = minQuantity;
        }
        if (nextQuantity > maxQuantity) {
            nextQuantity = maxQuantity;
        }

        nextQuantity = Math.round(nextQuantity * 100) / 100;

        setQuantity(nextQuantity);
        onQuantityChange(nextQuantity);
    };

    return (
        <StyledPoolTextfield
            {...poolTextfieldProps}
            startAdornment={
                <PoolIconButton
                    size="small"
                    onClick={() => updateQuantity(true, -interval)}
                    disabled={quantity <= minQuantity || props.disabled}
                >
                    <MinusIcon />
                </PoolIconButton>
            }
            endAdornment={
                <PoolIconButton
                    size="small"
                    onClick={() => updateQuantity(true, +interval)}
                    disabled={quantity >= maxQuantity || props.disabled}
                >
                    <PlusIcon />
                </PoolIconButton>
            }
            value={quantity}
            onChange={handleValueChange}
            onBlur={() => updateQuantity(false)}
        />
    );
};
